import type { ReactNode } from 'react';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { classNames } from '~/utils/classNames';

type AlertType = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  alignActions?: 'start' | 'center' | 'end';
  direction?: 'column' | 'row';
  type?: AlertType;
  className?: string;
  iconClassName?: string;
  onClose?: () => void;
}

const classes: Record<AlertType, { icon: string; title: string; wrapper: string }> = {
  neutral: {
    icon: 'i-ph:star text-bolt-ds-iconPrimary',
    title: 'text-bolt-ds-onNeutralContainer',
    wrapper: 'bg-bolt-ds-neutralContainer border-bolt-ds-neutralOutline',
  },
  info: {
    icon: 'i-ph:info text-bolt-ds-brand',
    title: 'text-bolt-ds-onBrandContainer',
    wrapper: 'bg-bolt-ds-brandContainer border-bolt-ds-brandOutline',
  },
  danger: {
    icon: 'i-ph:warning-circle text-bolt-ds-danger',
    title: 'text-bolt-ds-onDangerContainer',
    wrapper: 'bg-bolt-ds-dangerContainer border-bolt-ds-dangerOutline',
  },
  warning: {
    icon: 'i-ph:warning text-bolt-ds-warning',
    title: 'text-bolt-ds-onWarningContainer',
    wrapper: 'bg-bolt-ds-warningContainer border-bolt-ds-warningOutline',
  },
  success: {
    icon: 'i-ph:check-circle text-bolt-ds-success',
    title: 'text-bolt-ds-onSuccessContainer',
    wrapper: 'bg-bolt-ds-successContainer border-bolt-ds-successOutline',
  },
};

export const Alert = ({
  title,
  children,
  alignActions = 'start',
  direction = 'column',
  actions,
  type = 'neutral',
  className,
  iconClassName,
  onClose,
}: AlertProps) => {
  return (
    <div
      className={classNames(
        'relative flex items-start gap-3 p-4 border rounded-lg backdrop-blur-2xl',
        classes[type].wrapper,
        className,
      )}
    >
      {onClose && (
        <LegacyIconButton onClick={onClose} size="xs" className="absolute top-2 right-2">
          <span className="i-ph:x text-md" />
        </LegacyIconButton>
      )}

      <div
        className={classNames('w-5 h-5 shrink-0', iconClassName ?? classes[type].icon, {
          'mt-[2px]': !title,
        })}
      />

      <div
        className={classNames('flex w-full gap-2', {
          'flex-col': direction === 'column',
          '': direction === 'row',
        })}
      >
        <div className="flex flex-col w-full gap-1 items-start">
          {title && <span className={classNames('text-sm font-semibold', classes[type].title)}>{title}</span>}

          <div className="text-bolt-ds-textSecondary text-sm">{children}</div>
        </div>

        {actions && (
          <div
            className={classNames(
              'flex items-center gap-2 empty:hidden',
              direction === 'column' ? 'w-full' : undefined,
              direction === 'column'
                ? {
                    'justify-start': alignActions === 'start',
                    'justify-center': alignActions === 'center',
                    'justify-end': alignActions === 'end',
                  }
                : {
                    'items-start': alignActions === 'start',
                    'items-center': alignActions === 'center',
                    'items-end': alignActions === 'end',
                  },
            )}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};
